import { useFlag } from '@calendly/feature-check-react'
import { useCallback, useMemo } from 'react'

import { useEventType } from 'features/event_type_provider'
import {
  buildNotetakerEtToggleSavedTrackingPayload,
  isNotetakerEtSaveEligibleForLocationConfigurations,
} from 'features/growth_experiments'
import { useUpsertUserGuideStepMutation } from 'features/user_guide'
import { useTrack } from 'hooks/analytics_events'

import {
  getEventTypeLocks,
  getFormatValuesToServerData,
  getInitialValues,
  getPanelFieldsMapping,
  getValidationSchema
} from './utils'
import { useResetUpdateMutationsCache, useUpdateEventTypeMutation } from '../../../api'
import { useGetDefaultAvailabilityRules } from '../../../utils'
import { useSyncNotetakerSettings } from '../../hooks'
import { BaseSection } from '../base_section'
import { getEventDetailsPanelKeys } from '../components/event_details/utils'
import { Panels } from '../panels'
import { valuesMapper } from '../utils'

export const EditForm = () => {
  const eventType = useEventType()
  const defaultAvailabilityRules = useGetDefaultAvailabilityRules()
  const track = useTrack()
  const syncNotetakerSettings = useSyncNotetakerSettings()
  const [updateEventType, { isLoading, error }] = useUpdateEventTypeMutation({ id: eventType.id })
  const [upsertUserGuideStep] = useUpsertUserGuideStepMutation()

  const panelKeys = useMemo(() => getEventDetailsPanelKeys(eventType), [eventType])
  const etLocks = getEventTypeLocks(eventType)
  const lockingDisables = Object.values(etLocks).every(Boolean)

  const { isMETParent } = eventType

  const handleSuccess = useCallback((attributes) => {
    if (!isMETParent) {
      upsertUserGuideStep({ identifier: 'customize_and_share_first_et', status: 'completed' })
    }

    if (attributes.location_configurations_attributes.length > 0) {
      upsertUserGuideStep({ identifier: 'set_your_meeting_location', status: 'completed' })
    }
  }, [isMETParent, upsertUserGuideStep])

  const hasMsTeamsFlag = useFlag('ms_teams_notetaker')
  const handleSubmit = useCallback((attributes, values) => {
    if (isLoading) return Promise.resolve()

    const notetakerEnabledForEt = values?.notetakerEnabledForEt
    const hasSeenNotetakerToggle = Boolean(values?.hasSeenNotetakerToggle)
    const isNotetakerSaveEligible = isNotetakerEtSaveEligibleForLocationConfigurations(
      attributes.location_configurations_attributes || [],
      hasMsTeamsFlag
    )

    return updateEventType({
      eventType,
      attributes,
      onSuccess: () => {
        handleSuccess(attributes)

        if (typeof notetakerEnabledForEt === 'boolean' && isNotetakerSaveEligible) {
          syncNotetakerSettings({
            eventTypeId: eventType.id,
            notetakerEnabledForEt,
          })
        }

        if (hasSeenNotetakerToggle && typeof notetakerEnabledForEt === 'boolean' && isNotetakerSaveEligible) {
          const trackingPayload = buildNotetakerEtToggleSavedTrackingPayload({
            eventTypeId: eventType.id,
            flow: 'edit',
            isEnabled: notetakerEnabledForEt,
          })

          if (trackingPayload) {
            track(trackingPayload)
          }
        }
      }
    })
  }, [
    eventType,
    track,
    handleSuccess,
    isLoading,
    syncNotetakerSettings,
    updateEventType,
    hasMsTeamsFlag,
  ])

  useResetUpdateMutationsCache({ id: eventType.id })

  return (
    <BaseSection
      formatValues={getFormatValuesToServerData({ eventType })}
      initialValues={getInitialValues({ eventType, defaultAvailabilityRules })}
      panelFieldsMapping={getPanelFieldsMapping({ eventType })}
      validationSchema={getValidationSchema({ eventType })}
      valuesMapper={valuesMapper}
      externalError={error}
      onSubmit={handleSubmit}
    >
      <Panels
        panelKeys={panelKeys}
        etLocks={etLocks}
        lockingDisables={lockingDisables}
      />
    </BaseSection>
  )
}
